import { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Badge from "@mui/material/Badge";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PrintIcon from "@mui/icons-material/Print";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import { TransactionListRow } from "../../types/sale.type";
import { ReprintBillTarget, ReprintAuthRow } from "../../types/reprintAuth.type";
import {
  getCustomerEtaxEnabled,
  getListTransactions,
  getListReprintAuth,
} from "../../services";
import { usePdfDownload } from "../../utils/hooks/usePdfDownload";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { featureAccess } from "../../permissions/access";
import CreateGrantModal from "../ReprintAuth/CreateGrantModal";
import GrantDetailModal from "../ReprintAuth/GrantDetailModal";
import PrintHistoryModal from "../ReprintAuth/PrintHistoryModal";
import RevokeDialog from "../ReprintAuth/RevokeDialog";
import { formatNumber } from "../../utils";
import { formatDate } from "../../utils/date";
import {
  SALES_STATUS_COLOR,
  TRANSACTION_TYPE_COLOR,
  TRANSACTION_TYPE_LABEL,
  getPaymentChannelLabel,
  getStatusLabel,
} from "./data";
import PaymentEditDialog from "./PaymentEditDialog";
import SalesPagination from "./Pagination";
import ReplacedByInfo from "./ReplacedByInfo";
import BillNumberCell from "./BillNumberCell";
import TransactionsTableHead, { COLUMNS } from "./TransactionsTableHead";
import { TransactionListFilter } from "./salesListFilters";
import { billDetailPath } from "./salesListUrl";

interface TransactionsTableProps {
  listFilter: TransactionListFilter;
  /** A typed filter is still waiting out its debounce; the list is fetched once it settles. */
  isTypingSearch: boolean;
  triggerFetch: number;
  page: number;
  setPage: (page: number) => void;
  limit: number;
  setLimit: (limit: number) => void;
  /** The list's own query string, carried by each bill link so the bill page can lead back. */
  listSearch: string;
}

const renderSkeletonRows = (limit: number) => {
  return Array.from({ length: limit }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: COLUMNS }).map((__, colIndex) => (
        <TableCell key={colIndex}>
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  ));
};

/** A QR bill whose money the bank has not confirmed. */
const isUnsettledQr = (row: TransactionListRow) =>
  row.payment_type === "QR_CODE" && row.status === "pending";

export default function TransactionsTable({
  listFilter,
  isTypingSearch,
  triggerFetch,
  page,
  setPage,
  limit,
  setLimit,
  listSearch,
}: TransactionsTableProps) {
  const { downloadingId, downloadPdf, viewingId, openPdf } = usePdfDownload();
  const role = useUserRole();
  const canGrantReprint = featureAccess.reprintAuth(role);
  const canEditPayment = featureAccess.transactionPaymentEdit(role);
  // Per-row kebab menu (ดูบิลขาย / ดาวน์โหลดบิลขาย for all roles; เปิดสิทธิ์พิมพ์ซ้ำ
  // approver-only, available for any date including same-day bills — the normal
  // sales print flow is now quota-limited, so same-day bills also need a grant).
  // Reprint routes to CreateGrantModal or GrantDetailModal.
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<TransactionListRow | null>(null);
  const [grantBill, setGrantBill] = useState<ReprintBillTarget | null>(null);
  const [detailGrant, setDetailGrant] = useState<ReprintAuthRow | null>(null);
  const [revokeGrant, setRevokeGrant] = useState<ReprintAuthRow | null>(null);
  const [historyTarget, setHistoryTarget] = useState<{
    uuid: string;
    no: string;
  } | null>(null);
  const [paymentBill, setPaymentBill] = useState<TransactionListRow | null>(
    null,
  );
  // Bill whose existing-grant lookup is in flight (drives the menu-item spinner).
  const [lookupId, setLookupId] = useState<string | null>(null);
  const [rows, setRows] = useState<TransactionListRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const { status } = listFilter;

  // Fetches the list with the current filter values. Also run after a revoke or a
  // payment edit so the row's print_count / state reflect the change.
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: {
          data: { rows: data, count },
        },
      } = await getListTransactions({ ...listFilter, page, limit });

      setRows(data as TransactionListRow[]);
      setTotalCount(count);
    } finally {
      setLoading(false);
    }
  }, [listFilter, page, limit]);

  useEffect(() => {
    if (isTypingSearch) return;
    fetchList();
  }, [triggerFetch, isTypingSearch, fetchList]);

  const totalAmount = rows.reduce((sum, r) => sum + Number(r.grand_total), 0);

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    row: TransactionListRow,
  ) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  // Both the Sales list and the reprint API use canonical bill_type_enum values
  // ("cashsale"/"invoice"), so the bill type passes straight through.
  const toBillTarget = (row: TransactionListRow, isEtaxShop: boolean): ReprintBillTarget => ({
    transaction_uuid: row.uuid,
    transaction_no: row.transaction_no,
    transaction_type: row.transaction_type === "cashsale" ? "cashsale" : "invoice",
    customer_name: row.customer_name,
    customer_code: row.customer_code,
    is_etax_shop: isEtaxShop,
  });

  // Mirror the prototype's openCreateFor: if the bill already has an ACTIVE
  // grant, open its detail modal; otherwise open the create-grant modal. The
  // menu stays open with a spinner while the lookup is in flight; errors surface
  // through the shared axios interceptor.
  const handleOpenReprint = async (row: TransactionListRow) => {
    setLookupId(row.uuid);
    try {
      const {
        data: { data },
      } = await getListReprintAuth({
        status: "all",
        q: row.transaction_no,
        limit: 20,
        page: 1,
      });
      const grants = ((data.rows ?? []) as ReprintAuthRow[]).filter(
        (g) => g.transaction_uuid === row.uuid,
      );
      const activeGrant = grants.find((g) => g.status === "ACTIVE");
      if (activeGrant) {
        handleCloseMenu();
        setDetailGrant(activeGrant);
        return;
      }
      // A new grant offers only the copies the shop prints, so its e-Tax flag is needed first.
      const isEtaxShop = await getCustomerEtaxEnabled(row.customer_code, row.company_id);
      handleCloseMenu();
      setGrantBill(toBillTarget(row, isEtaxShop));
    } catch {
      // Error already surfaced by the axios interceptor.
      handleCloseMenu();
    } finally {
      setLookupId(null);
    }
  };

  return (
    <>
      <TableContainer>
        <Table>
          <TransactionsTableHead />
          <TableBody>
            {loading && renderSkeletonRows(limit)}
            {!loading &&
              rows.map((row, index) => (
                <TableRow
                  key={row.uuid}
                  sx={{
                    bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                    "& td, & th": { padding: "8px" },
                  }}
                >
                  <BillNumberCell
                    transactionNo={row.transaction_no}
                    billPath={billDetailPath(row, listSearch)}
                  />
                  <TableCell>{formatDate(row.transaction_date)}</TableCell>
                  <TableCell>{row.customer_code}</TableCell>
                  <TableCell>
                    {row.customer_name}
                    <ReplacedByInfo replacedBy={row.replaced_by} />
                  </TableCell>
                  <TableCell>{row.inv_taxid || "-"}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        bgcolor:
                          TRANSACTION_TYPE_COLOR[row.transaction_type] + "1A",
                        color: TRANSACTION_TYPE_COLOR[row.transaction_type],
                        borderRadius: 4,
                        px: 1.5,
                        py: 0.5,
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {TRANSACTION_TYPE_LABEL[row.transaction_type]}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          flexShrink: 0,
                          borderRadius: "50%",
                          bgcolor: SALES_STATUS_COLOR[row.status],
                        }}
                      />
                      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                        {getStatusLabel(row.status)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                        {getPaymentChannelLabel(row.payment_type)}
                      </Typography>
                      {/* `pending` on a QR bill does not mean "awaiting review" as it
                          does everywhere else in this column's neighbour — it means the
                          bank has not confirmed the transfer, and the API leaves such a
                          bill out of เงินโอน until it does. These rows are exactly the
                          gap between the report's total and the day's real takings. */}
                      {isUnsettledQr(row) && (
                        <Tooltip title="ยังไม่พบเงินเข้า — ยอดนี้ยังไม่ถูกนับในเงินโอน">
                          <ErrorOutlineIcon
                            sx={{ fontSize: 16, color: "warning.main" }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(Number(row.grand_total))}
                  </TableCell>
                  <TableCell align="center">
                    {row.print_count == null ? (
                      "-"
                    ) : (
                      <Tooltip title="ดูประวัติการพิมพ์บิล">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setHistoryTarget({
                              uuid: row.uuid,
                              no: row.transaction_no,
                            })
                          }
                        >
                          <Badge
                            badgeContent={row.print_count}
                            color={row.print_count >= 2 ? "error" : "primary"}
                            showZero
                          >
                            <PrintIcon fontSize="small" />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="ดาวน์โหลดบิลขาย">
                      <span>
                        <IconButton
                          size="small"
                          disabled={downloadingId === row.uuid}
                          onClick={() => downloadPdf(row.uuid)}
                        >
                          {downloadingId === row.uuid ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DownloadIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="จัดการ">
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenMenu(e, row)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูล
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {(status === "pending" || status === "approve" || status === "paid") && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "16px 8px",
            bgcolor: "#FFCF8F36",
          }}
        >
          <Typography variant="h6" color="primary" fontWeight={600}>
            ผลรวม {totalCount} รายการ
          </Typography>
          <Typography variant="h6" color="primary" fontWeight={600}>
            {formatNumber(totalAmount)} บาท
          </Typography>
        </Box>
      )}
      <SalesPagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
      >
        <MenuItem
          disabled={!menuRow || viewingId === menuRow.uuid}
          onClick={() => {
            if (menuRow) openPdf(menuRow.uuid);
            handleCloseMenu();
          }}
        >
          {menuRow && viewingId === menuRow.uuid ? (
            <CircularProgress size={16} sx={{ mr: 1 }} />
          ) : (
            <VisibilityOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          )}
          ดูบิลขาย
        </MenuItem>
        {canGrantReprint && menuRow && (
          <MenuItem
            disabled={!menuRow || lookupId === menuRow.uuid}
            onClick={() => {
              if (menuRow) handleOpenReprint(menuRow);
            }}
          >
            {menuRow && lookupId === menuRow.uuid ? (
              <CircularProgress size={16} sx={{ mr: 1 }} />
            ) : (
              <ReceiptLongIcon fontSize="small" sx={{ mr: 1 }} />
            )}
            เปิดสิทธิ์พิมพ์ซ้ำ
          </MenuItem>
        )}
        {canEditPayment && menuRow && (
          <MenuItem
            onClick={() => {
              setPaymentBill(menuRow);
              handleCloseMenu();
            }}
          >
            <PaymentsOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
            แก้ไขการชำระเงิน
          </MenuItem>
        )}
      </Menu>

      <CreateGrantModal
        open={Boolean(grantBill)}
        bill={grantBill}
        onClose={() => setGrantBill(null)}
        onCreated={() => setGrantBill(null)}
      />

      <GrantDetailModal
        open={Boolean(detailGrant)}
        grant={detailGrant}
        onClose={() => setDetailGrant(null)}
        onOpenPrintHistory={(grant) => {
          setDetailGrant(null);
          setHistoryTarget({
            uuid: grant.transaction_uuid,
            no: grant.transaction_no,
          });
        }}
        onRevoke={(grant) => {
          setDetailGrant(null);
          setRevokeGrant(grant);
        }}
      />

      <RevokeDialog
        open={Boolean(revokeGrant)}
        grant={revokeGrant}
        onClose={() => setRevokeGrant(null)}
        onRevoked={fetchList}
      />

      <PrintHistoryModal
        open={Boolean(historyTarget)}
        transactionUuid={historyTarget?.uuid ?? null}
        transactionNo={historyTarget?.no}
        onClose={() => setHistoryTarget(null)}
      />

      <PaymentEditDialog
        open={Boolean(paymentBill)}
        bill={paymentBill}
        onClose={() => setPaymentBill(null)}
        onSaved={() => {
          setPaymentBill(null);
          fetchList();
        }}
      />
    </>
  );
}
